// Las lecturas de la ficha 360 del cliente, separadas por permiso: el resumen y el nombre de la
// tienda de alta fallan por su cuenta y ninguna vacía a la otra.
import { useQuery } from '@tanstack/react-query';
import {
    getCustomer360,
    type Customer360,
} from '@/api/services/retail-customer/retail-customer.client';
import { getStore } from '@/api/services/retail-org/retail-org.client';
import { readFailureOf, type ReadFailure } from '../lib/creditCustomersErrors';

const KEY = ['credit-admin', 'customer-360'] as const;

const STALE_TIME_MS = 60_000;

/** El resumen 360 del cliente abierto: `undefined` mientras vuela, y la falla dice de qué tipo es. */
export interface CustomerSummaryState {
    data: Customer360 | undefined;
    isLoading: boolean;
    failure: ReadFailure | null;
    retry: () => void;
}

/**
 * `GET /customers/{id}/summary360` — la única lectura que alimenta Perfil, Cuenta PCF y el origen.
 * Permiso `retail.customer.summary.read`, SIN scope: no depende del negocio del cliente.
 */
export function useCustomer360(retailCustomerId: string | null): CustomerSummaryState {
    const query = useQuery({
        queryKey: [...KEY, retailCustomerId],
        queryFn: () => getCustomer360(retailCustomerId ?? ''),
        enabled: retailCustomerId !== null,
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    return {
        data: query.data,
        // `isPending` de una query deshabilitada es `true` para siempre: sin cliente abierto no hay
        // nada en vuelo, y decir «cargando» dejaría la ficha girando sin fin.
        isLoading: retailCustomerId !== null && query.isPending,
        failure: readFailureOf(query.error),
        retry: () => {
            void query.refetch();
        },
    };
}

/** El nombre de la tienda de alta. Su permiso es del directorio de negocios, no el de la ficha. */
export interface OriginStoreState {
    name: string | undefined;
    /** Hay tienda que resolver pero ya se sabe que no hay negocio: la lectura ni sale. */
    hasNoScope: boolean;
    isLoading: boolean;
    failure: ReadFailure | null;
}

/**
 * `GET /retailers/{retailerId}/stores/{storeId}` — la ficha guarda el id de la tienda donde se dio
 * de alta al cliente, y el nombre vive en el directorio. Pide `retail.store.read`.
 * `isScopePending` = el negocio todavía se está resolviendo: se reporta cargando, no «sin negocio».
 */
export function useOriginStore(
    retailerId: string | null,
    originStoreId: string | null,
    isScopePending = false,
): OriginStoreState {
    const enabled = retailerId !== null && originStoreId !== null;
    const lacksScope = retailerId === null && originStoreId !== null;

    const query = useQuery({
        queryKey: [...KEY, 'origin-store', retailerId, originStoreId],
        queryFn: () => getStore(retailerId ?? '', originStoreId ?? ''),
        enabled,
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    return {
        name: query.data?.name,
        hasNoScope: lacksScope && !isScopePending,
        isLoading: (enabled && query.isPending) || (lacksScope && isScopePending),
        failure: readFailureOf(query.error),
    };
}
